/** Consulta assistida de CNPJ (EMP-02): não grava cadastro nem determina papéis. */
import { z } from 'zod'
import { documento } from './cadastro.js'
import { endereco } from './entidades.js'
import { uuid, uuidDeRota } from './comum.js'

/** A máscara pode sair; as letras do CNPJ alfanumérico permanecem. DV é validado no servidor. */
export const cnpjDaConsulta = z.string().max(32)
  .transform((valor) => valor.replace(/[\s./-]/g, '').toUpperCase())
  .pipe(documento.regex(/^[A-Z0-9]{12}[0-9]{2}$/, 'Informe os 14 caracteres do CNPJ.'))

/**
 * Para que a consulta serve (adendo de 29/10/2026). A finalidade só escolhe QUAL permissão o servidor
 * exige e se há orientação de duplicidade; acesso nunca nasce do parâmetro. Ausente é `cadastro`, o
 * comportamento de antes. Os alvos dizem criação × edição sem ambiguidade: sem id é criação; com id,
 * a permissão é a de editar e o alvo é conferido. Id que a finalidade não aceita é recusado, não ignorado.
 */
export const FINALIDADES_DA_CONSULTA_DE_CNPJ = ['cadastro', 'edicao', 'perfil', 'fornecedor', 'entidade'] as const
export const finalidadeDaConsultaDeCnpj = z.enum(FINALIDADES_DA_CONSULTA_DE_CNPJ)
export type FinalidadeDaConsultaDeCnpj = z.infer<typeof finalidadeDaConsultaDeCnpj>

export const buscaDaConsultaDeCnpj = z.object({
  finalidade: finalidadeDaConsultaDeCnpj.default('cadastro'),
  // O mesmo validador do `:id` das rotas: o alvo é um registro existente, não um id a nascer.
  empresa_id: uuidDeRota.optional(),
  entidade_id: uuidDeRota.optional(),
}).strict().superRefine((busca, ctx) => {
  const aceitaEmpresa = busca.finalidade === 'edicao' || busca.finalidade === 'perfil'
  if (busca.empresa_id !== undefined && !aceitaEmpresa) {
    ctx.addIssue({ code: 'custom', path: ['empresa_id'], message: 'Esta finalidade não recebe empresa.' })
  }
  if (busca.finalidade === 'edicao' && busca.empresa_id === undefined) {
    ctx.addIssue({ code: 'custom', path: ['empresa_id'], message: 'A edição informa a empresa editada.' })
  }
  if (busca.entidade_id !== undefined && busca.finalidade !== 'entidade') {
    ctx.addIssue({ code: 'custom', path: ['entidade_id'], message: 'Esta finalidade não recebe entidade.' })
  }
})
export type BuscaDaConsultaDeCnpj = z.infer<typeof buscaDaConsultaDeCnpj>

export const dadosDeCnpj = z.object({
  cnpj: cnpjDaConsulta,
  razaoSocial: z.string().nullable(),
  nomeFantasia: z.string().nullable(),
  inscricaoEstadual: z.string().nullable(),
  inscricaoMunicipal: z.string().nullable(),
  endereco,
})
export type DadosDeCnpj = z.infer<typeof dadosDeCnpj>

/** Falha externa, ausência e resposta incompleta são estados diferentes; manual continua livre. */
export const respostaDeCnpj = z.discriminatedUnion('estado', [
  z.object({ estado: z.literal('encontrado'), dados: dadosDeCnpj }),
  z.object({ estado: z.literal('incompleto'), dados: dadosDeCnpj }),
  z.object({ estado: z.literal('ja_cadastrada'), empresa: z.object({ id: uuid, nome: z.string() }) }),
  z.object({ estado: z.literal('nao_encontrado') }),
  z.object({
    estado: z.literal('limite'),
    origem: z.enum(['local', 'provedor']),
    tenteNovamenteEmSegundos: z.number().int().min(1).max(3600),
  }),
  z.object({ estado: z.literal('tempo_esgotado') }),
  z.object({
    estado: z.literal('indisponivel'),
    motivo: z.enum(['provedor', 'rede', 'resposta_invalida', 'nao_suportado']),
  }),
])
export type RespostaDeCnpj = z.infer<typeof respostaDeCnpj>
